import { useMemo, useCallback } from "react"
import { map, find, chain, keys, keyBy, capitalize, toLower } from "lodash"
import PropTypes from "prop-types"
import { Paper } from "@mui/material"
import { UUID, RESOURCE, PERMISSION, ROLE } from "helpers/shapes"
import { ALL_DATASET_ACCESS_TEAM } from "helpers/datasets"
import { PERMISSION_LEVELS, PERMISSION_LEVELS_AS_OPTIONS } from "helpers/permissions"
import { getResourceLabel } from "helpers/permissions/resource-types"
import { isAllAccessTeam } from "helpers/permissions/teams"
import UpsellTabs, { UPSELL_TABS_SHAPE } from "components/material/upsell-tabs"
import Table from "components/material/table"

export default function TeamResources({ account, teamId, teamAlias, role, teamRole, resources, permissions, tabs, update }) {
  const hasWriteAccess = (role === "admin" || teamRole === "ADMIN") && !isAllAccessTeam(teamAlias)

  const getResourceType = useCallback((resourceId) => {
    return chain(resources)
      .find(({ id }) => id === resourceId)
      .get("type")
      .value()
  }, [resources])

  const resourcesById = useMemo(() => keyBy(resources, "id"), [resources])

  const optionLabels = useMemo(() => chain(resourcesById)
    .pickBy(({ id, permissionLevel }) => !find(permissions, p => p.id === id) && permissionLevel === "OWNER")
    .mapValues(getResourceLabel)
    .value(),
  [permissions, resourcesById])

  const addForm = useMemo(() => {
    if (!hasWriteAccess) {
      return null
    }

    function onAdd(data, cb) {
      const query = `mutation ($teamId: IdOrAlias!, $permissionLevel: PermissionLevel!) {
        ${map(data.resources, (resourceId, index) => `resource${index}: updatePermissions(resourceType: ${getResourceType(resourceId)}, resourceId: "${resourceId}", permissions: { teamId: $teamId, permissionLevel: $permissionLevel }) { id }`)}
      }`

      const variables = { permissionLevel: data.permissionLevel, teamId }

      update(query, variables, account, {
        reloadData: "teamPermissions",
      }, cb)
    }

    return {
      title: "Add Team Permissions",
      submitText: "Add Permissions",
      track: {
        type: "team_permission_added",
        payload: ({ resources, permissionLevel }) => ({
          resources: map(resources, id => resourcesById[id].name),
          permissionLevel,
        }),
      },
      onSubmit: onAdd,
      fields: [{
        id: "resources",
        label: "Resources",
        type: "multi",
        options: keys(optionLabels),
        optionLabels,
        validations: {
          isRequired: true,
        },
        sort: ([, lbl1], [, lbl2]) => lbl1.localeCompare(lbl2),
      }, {
        id: "permissionLevel",
        label: "Permission Level",
        type: "select",
        options: keys(PERMISSION_LEVELS),
        optionLabels: PERMISSION_LEVELS_AS_OPTIONS,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [account, hasWriteAccess, teamId, update, getResourceType, resourcesById, optionLabels])

  const editForm = useMemo(() => {
    function onEdit(data, cb) {
      const query = `mutation ($teamId: IdOrAlias!, $resourceId: IdOrShortIdOrAlias!, $resourceType: ResourceType!, $permissionLevel: PermissionLevel!) {
        updatePermissions(resourceType: $resourceType, resourceId: $resourceId, permissions: { teamId: $teamId, permissionLevel: $permissionLevel }) { id }
      }`

      const variables = {
        resourceId: data.id,
        resourceType: getResourceType(data.id),
        permissionLevel: data.permissionLevel,
        teamId,
      }

      update(query, variables, account, {
        reloadData: "teamPermissions",
      }, cb)
    }

    return {
      title: "Edit Team Permission Level",
      submitText: "Save",
      track: {
        type: "team_permission_level_edited",
        payload: ({ id, permissionLevel }) => ({
          resource: resourcesById[id].name,
          permissionLevel,
        }),
      },
      onSubmit: onEdit,
      isEnabled: () => hasWriteAccess,
      getInitialValues: ({ permissionLevel }) => ({ permissionLevel }),
      fields: [{
        id: "permissionLevel",
        label: "Permission Level",
        type: "select",
        options: keys(PERMISSION_LEVELS),
        optionLabels: PERMISSION_LEVELS_AS_OPTIONS,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [account, hasWriteAccess, teamId, update, getResourceType, resourcesById])

  const deleteForm = useMemo(() => {
    function onDelete(data, cb) {
      const query = `mutation ($teamId: IdOrAlias!) {
        ${map(data, (resource, index) => `resource${index}: updatePermissions(resourceType: ${resource.type}, resourceId: "${resource.id}", permissions: { teamId: $teamId, permissionLevel: NONE }) { id }`)}
      }`

      const variables = { teamId }

      update(query, variables, account, {
        reloadData: "teamPermissions",
      }, cb)
    }

    return {
      type: "permission",
      track: {
        type: "remove_team_permission",
        payload: ({ name }) => ({ resource: name }),
      },
      isEnabled: () => hasWriteAccess,
      renderItem: resource => <li key={resource.id}>{resource.name}</li>,
      onSubmit: onDelete,
    }
  }, [account, hasWriteAccess, teamId, update])

  // We wait for team alias or return null if this is the all dataset access team
  if (!teamAlias || toLower(teamAlias) === ALL_DATASET_ACCESS_TEAM.ALIAS) {
    return null
  }

  return <div className="modules_team-resources">
    <UpsellTabs id="team-resources" tabs={tabs} />
    <Paper className="content" square>
      <Table
        title="Team Permissions"
        idField="id"
        addForm={addForm}
        editForm={editForm}
        deleteForm={deleteForm}
        data={permissions}
        columns={[{
          name: "Name",
          id: "name",
        }, {
          name: "Type",
          id: "type",
          formatter: capitalize,
        }, {
          name: "Description",
          id: "description",
        }, {
          name: "Permission Level",
          id: "permissionLevel",
          formatter: capitalize,
        }]}
      />
    </Paper>
  </div>
}

TeamResources.propTypes = {
  account: PropTypes.string.isRequired,
  role: ROLE.isRequired,
  teamRole: PropTypes.oneOf(["ADMIN", "MEMBER"]),
  teamAlias: PropTypes.string.isRequired,
  teamId: UUID,
  permissions: PropTypes.arrayOf(PERMISSION),
  resources: PropTypes.arrayOf(RESOURCE).isRequired,
  update: PropTypes.func.isRequired,
  tabs: UPSELL_TABS_SHAPE,
}
